import { format, differenceInDays, isValid } from 'date-fns';

// Helper function to safely format dates
const safeFormatDate = (date, formatString) => {
  if (!date) return 'N/A';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  return isValid(dateObj) ? format(dateObj, formatString) : 'Invalid date';
};

/**
 * Prints an itinerary with all details including travel tips and essential information
 * @param {Object} itinerary - The itinerary object to print
 * @param {Object} user - Optional user object for personalization
 */
export const printItinerary = (itinerary, user = null) => {
  if (!itinerary) {
    console.error("Cannot print: itinerary data is unavailable");
    return;
  }

  // Create a hidden print-optimized version of the itinerary
  const printContent = document.createElement('div');
  printContent.className = 'print-content';
  
  // Add styles for print content
  const style = document.createElement('style');
  style.textContent = `
    @media print {
      body * {
        visibility: hidden;
      }
      .print-content, .print-content * {
        visibility: visible;
      }
      @page {
        size: A4;
        margin: 1.5cm;
      }
      .print-content {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        padding: 0;
        margin: 0;
        font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        color: #333;
        font-size: 10pt;
        background-color: white;
      }
      
      /* Fix for empty last page */
      html, body {
        height: auto !important;
        overflow: visible !important;
      }
      
      /* Explicitly set day containers to start on new pages */
      .day-container {
        page-break-before: auto;
        page-break-after: always;
      }
      .day-container:first-of-type {
        page-break-before: auto;
      }
      .day-container + .day-container {
        page-break-before: always;
      }
      
      /* Keep day content together */
      .day-content {
        page-break-inside: avoid;
      }
      
      /* Document Header */
      .header-container {
        background: linear-gradient(90deg, #1A237E 0%, #3949AB 100%);
        color: white;
        padding: 0.8cm 1cm 0.4cm;
        position: relative;
      }
      .print-logo {
        display: flex;
        align-items: center;
        margin-bottom: 5px;
      }
      .logo-icon {
        font-size: 20px;
        margin-right: 8px;
      }
      .logo-text {
        font-weight: bold;
        font-size: 22px;
        color: white;
      }
      .destination-title {
        font-size: 28px;
        font-weight: 700;
        margin: 10px 0 5px;
        color: white;
        text-shadow: 0 1px 2px rgba(0,0,0,0.2);
      }
      .date-range {
        font-size: 14px;
        color: rgba(255,255,255,0.9);
        margin-bottom: 8px;
        font-weight: 500;
      }
      
      /* Trip details bar */
      .trip-details {
        background-color: white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        margin: 0;
        padding: 12px 1cm;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #eaeaea;
      }
      .detail-item {
        display: flex;
        align-items: center;
        font-size: 13px;
        color: #444;
        flex: 1;
        min-width: 120px;
        max-width: 200px;
        padding: 6px 0;
      }
      .detail-icon {
        margin-right: 10px;
        font-size: 18px;
        color: #3949AB;
      }
      
      /* Document body */
      .document-body {
        padding: 0.5cm 1cm 1cm;
      }
      
      /* User info */
      .user-info {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-bottom: 15px;
      }
      .user-avatar {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background-color: #FFF;
        color: #3949AB;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 8px;
        font-size: 14px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      }
      .user-name {
        font-size: 13px;
        font-weight: 500;
        color: white;
      }
      
      /* Day headers and sections */
      .day-section {
        margin-bottom: 15px;
      }
      .section-title {
        color: #1A237E;
        font-size: 18px;
        font-weight: 600;
        margin: 20px 0 12px;
        padding-bottom: 6px;
        border-bottom: 2px solid #3949AB;
      }
      .day-title {
        background: linear-gradient(90deg, #3949AB 0%, #5C6BC0 100%);
        color: white;
        padding: 12px 16px;
        border-radius: 6px 6px 0 0;
        font-weight: 600;
        margin: 25px 0 0 0;
        font-size: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      }
      .day-title .day-number {
        font-weight: bold;
        font-size: 18px;
      }
      .day-title .day-date {
        font-size: 14px;
        font-weight: 400;
      }
      .day-content {
        background-color: #fff;
        border: 1px solid #e0e0e0;
        border-top: none;
        border-radius: 0 0 6px 6px;
        padding: 16px;
        margin-bottom: 25px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        page-break-inside: avoid;
      }
      .day-break {
        page-break-before: auto;
      }
      .force-page-break {
        page-break-before: always;
      }
      
      /* Activity styling */
      .time-group {
        margin-bottom: 20px;
        page-break-inside: avoid;
      }
      .time-group-heading {
        color: #3949AB;
        font-weight: 600;
        margin: 15px 0 10px;
        padding-bottom: 5px;
        border-bottom: 1px solid #e0e0e0;
        font-size: 14px;
      }
      .activity-section {
        margin-bottom: 15px;
        page-break-inside: avoid;
      }
      .activity-card {
        background-color: #fff;
        border-radius: 6px;
        padding: 12px 15px;
        margin-bottom: 10px;
        border: 1px solid #e0e0e0;
        position: relative;
        overflow: hidden;
      }
      .activity-card::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 5px;
      }
      .activity-card.attraction::before {
        background-color: #2196F3;
      }
      .activity-card.dining::before {
        background-color: #4CAF50;
      }
      .activity-card.activity::before {
        background-color: #FF9800;
      }
      .activity-card.transportation::before {
        background-color: #9C27B0;
      }
      .activity-time {
        color: #3949AB;
        font-weight: bold;
        margin-bottom: 4px;
        font-size: 13px;
      }
      .activity-name {
        font-weight: 600;
        margin: 4px 0;
        color: #212121;
        font-size: 15px;
      }
      .activity-description {
        margin-top: 4px;
        line-height: 1.4;
        color: #444;
        font-size: 12px;
      }
      .travel-info-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
        margin: 10px 0;
      }
      .travel-info-item {
        background-color: #f8f9fa;
        border-radius: 6px;
        padding: 8px 10px;
        border-left: 3px solid #5E17EB;
      }
      .travel-info-title {
        font-weight: bold;
        color: #5E17EB;
        margin-bottom: 4px;
        font-size: 12px;
      }
      .travel-info-content {
        line-height: 1.3;
        font-size: 11px;
      }
      .footer {
        text-align: center;
        font-size: 10px;
        color: #888;
        margin-top: 15px;
        padding-top: 8px;
        border-top: 1px solid #ddd;
        display: flex;
        flex-direction: column;
        gap: 3px;
        page-break-inside: avoid;
        page-break-after: avoid;
      }
      .print-disclaimer {
        max-width: 90%;
        margin: 0 auto;
        text-align: center;
        font-size: 9px;
        color: #888;
      }
      .cost-indicator {
        display: inline-block;
        color: #4caf50;
        font-weight: bold;
        font-size: 0.9em;
        margin-left: 6px;
      }
      .activity-meta {
        display: flex;
        justify-content: space-between;
        margin-top: 3px;
        font-size: 10px;
        color: #666;
      }
      .activity-type {
        display: inline-block;
        padding: 2px 6px;
        background-color: #f0e6ff;
        color: #5E17EB;
        border-radius: 12px;
        font-size: 9px;
        font-weight: 500;
      }
      .supplementary-section {
        page-break-inside: avoid;
        page-break-after: avoid;
      }
      
      /* Travel tips */
      .tips-section {
        margin: 25px 0;
        background-color: #F5F5F5;
        border-radius: 6px;
        padding: 15px 20px;
      }
      .tip-list {
        padding-left: 25px;
        margin: 10px 0;
      }
      .tip-list li {
        margin-bottom: 8px;
        line-height: 1.4;
        font-size: 12px;
        color: #444;
      }
      
      /* Travel information grid */
      .travel-info-section {
        margin: 25px 0;
      }
      .travel-info-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 15px;
        margin: 15px 0;
      }
      .travel-info-item {
        background-color: #fff;
        border-radius: 6px;
        padding: 12px 15px;
        border: 1px solid #e0e0e0;
        position: relative;
        box-shadow: 0 1px 2px rgba(0,0,0,0.05);
      }
      .travel-info-title {
        font-weight: bold;
        color: #3949AB;
        margin-bottom: 6px;
        font-size: 14px;
        border-bottom: 1px solid #eaeaea;
        padding-bottom: 5px;
        display: flex;
        align-items: center;
      }
      .travel-info-title::before {
        content: '';
        display: inline-block;
        width: 12px;
        height: 12px;
        margin-right: 8px;
        background-color: #3949AB;
        border-radius: 50%;
      }
      .travel-info-content {
        line-height: 1.4;
        font-size: 12px;
        color: #444;
      }
      
      /* Activity metadata */
      .activity-meta {
        display: flex;
        justify-content: space-between;
        margin-top: 8px;
        font-size: 11px;
        color: #666;
        padding-top: 6px;
        border-top: 1px dashed #eaeaea;
      }
      .cost-indicator {
        display: inline-flex;
        align-items: center;
        color: #4caf50;
        font-weight: 500;
        font-size: 11px;
      }
      .cost-indicator::before {
        content: '•';
        font-size: 16px;
        margin-right: 4px;
      }
      .activity-type {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 10px;
        font-weight: 500;
        background-color: #E8EAF6;
        color: #3949AB;
      }
      
      /* Footer styling */
      .footer {
        text-align: center;
        margin-top: 30px;
        padding-top: 15px;
        border-top: 1px solid #eaeaea;
        display: flex;
        flex-direction: column;
        gap: 5px;
        font-size: 11px;
        color: #757575;
        page-break-inside: avoid;
        page-break-after: avoid;
      }
      .footer-logo {
        font-weight: bold;
        font-size: 14px;
        color: #3949AB;
        margin-bottom: 5px;
      }
      .print-disclaimer {
        max-width: 85%;
        margin: 8px auto 0;
        text-align: center;
        font-size: 9px;
        color: #9e9e9e;
        font-style: italic;
      }
      .supplementary-section {
        page-break-inside: avoid;
        page-break-after: avoid;
      }
      
      /* Trip details - updated for the right column list */
      .trip-details-list {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        text-align: right;
      }
      .detail-item {
        display: flex;
        align-items: center;
        font-size: 12px;
        color: rgba(255,255,255,0.9);
        padding: 3px 0;
      }
      .detail-icon {
        margin-right: 6px;
        font-size: 16px;
      }
    }
  `;
  document.head.appendChild(style);
  
  // Create header with logo and trip details
  const header = document.createElement('div');
  header.className = 'header-container';
  
  // Add a row container for header content
  const headerRow = document.createElement('div');
  headerRow.style.display = 'flex';
  headerRow.style.justifyContent = 'space-between';
  headerRow.style.alignItems = 'flex-start';
  
  // Left column for logo and title
  const leftColumn = document.createElement('div');
  
  // Logo and branding
  const logoDiv = document.createElement('div');
  logoDiv.className = 'print-logo';
  logoDiv.innerHTML = `
    <span class="logo-icon">✈️</span>
    <span class="logo-text">roamzap</span>
  `;
  leftColumn.appendChild(logoDiv);
  
  const destinationTitle = document.createElement('h1');
  destinationTitle.className = 'destination-title';
  destinationTitle.textContent = `${itinerary.destination} Itinerary`;
  leftColumn.appendChild(destinationTitle);
  
  const dateRange = document.createElement('p');
  dateRange.className = 'date-range';
  dateRange.textContent = `${safeFormatDate(itinerary.startDate, 'MMMM d')} - ${safeFormatDate(itinerary.endDate, 'MMMM d, yyyy')}
    (${isValid(new Date(itinerary.startDate)) && isValid(new Date(itinerary.endDate)) ? 
      differenceInDays(new Date(itinerary.endDate), new Date(itinerary.startDate)) + 1 : '?'} days)`;
  leftColumn.appendChild(dateRange);
  
  headerRow.appendChild(leftColumn);
  
  // Right column for trip details
  const rightColumn = document.createElement('div');
  rightColumn.style.textAlign = 'right';
  rightColumn.style.paddingTop = '10px';
  
  // Add user information if available
  if (user) {
    const userInfo = document.createElement('div');
    userInfo.className = 'user-info';
    
    // Create user avatar (either photo or first letter of name)
    const userAvatar = document.createElement('div');
    userAvatar.className = 'user-avatar';
    
    if (user.photoURL) {
      userAvatar.innerHTML = `<img src="${user.photoURL}" alt="${user.displayName || 'User'}" style="width: 100%; height: 100%; border-radius: 50%;">`;
    } else {
      const displayName = user.displayName || (user.email && user.email.split('@')[0]) || 'User';
      userAvatar.textContent = displayName.charAt(0).toUpperCase();
    }
    
    // Create user name element
    const userName = document.createElement('div');
    userName.className = 'user-name';
    userName.textContent = `${user.displayName || (user.email && user.email.split('@')[0]) || 'User'}'s Itinerary`;
    
    userInfo.appendChild(userAvatar);
    userInfo.appendChild(userName);
    rightColumn.appendChild(userInfo);
  }
  
  // Trip details list
  const tripDetailsList = document.createElement('div');
  tripDetailsList.className = 'trip-details-list';
  
  const detailsIcons = {
    travelers: '👥',
    budget: '💰',
    interests: '❤️'
  };
  
  // Create trip detail items
  const detailItems = [
    { key: 'travelers', text: `${itinerary.travelers} Traveler${itinerary.travelers !== 1 ? 's' : ''}` },
    { key: 'budget', text: `Budget: ${itinerary.budget.charAt(0).toUpperCase() + itinerary.budget.slice(1)}` }
  ];
  
  if (Array.isArray(itinerary.interests) && itinerary.interests.length > 0) {
    detailItems.push({ 
      key: 'interests', 
      text: `Interests: ${itinerary.interests.slice(0, 3).join(', ')}${itinerary.interests.length > 3 ? '...' : ''}`
    });
  }
  
  detailItems.forEach(item => {
    const detailItem = document.createElement('div');
    detailItem.className = 'detail-item';
    detailItem.innerHTML = `<span class="detail-icon">${detailsIcons[item.key]}</span> ${item.text}`;
    tripDetailsList.appendChild(detailItem);
  });
  
  rightColumn.appendChild(tripDetailsList);
  headerRow.appendChild(rightColumn);
  
  header.appendChild(headerRow);
  printContent.appendChild(header);
  
  // Document body container
  const documentBody = document.createElement('div');
  documentBody.className = 'document-body';
  
  // Get itinerary length to calculate when to force page breaks
  const daysCount = itinerary.days?.length || 0;
  // const dayBreakInterval = daysCount > 4 ? 2 : 3; // Force page break every 2-3 days based on itinerary length

  // Predictive height estimation for page breaks
  //   const estimateDayHeight = (day) => {
  //     if (!day.activities || !day.activities.length) return 200; // Empty day height estimation
  //     
  //     // Base height for day title, container padding, margins and borders
  //     let estimatedHeight = 200; // Increased from 120 to account for day header + padding + margins
  //     
  //     // Group activities to calculate more accurately
  //     const timeGroups = { 'Morning': [], 'Afternoon': [], 'Evening': [] };
  //     
  //     day.activities.forEach(activity => {
  //       const time = (activity.time || "").toLowerCase();
  //       if (time.includes('morning') || (parseInt(time) >= 6 && parseInt(time) < 12)) {
  //         timeGroups['Morning'].push(activity);
  //       } else if (time.includes('afternoon') || (parseInt(time) >= 12 && parseInt(time) < 18)) {
  //         timeGroups['Afternoon'].push(activity);
  //       } else {
  //         timeGroups['Evening'].push(activity);
  //       }
  //     });
  //     
  //     // Add height for each time group that has activities
  //     Object.keys(timeGroups).forEach(timeGroup => {
  //       if (timeGroups[timeGroup].length > 0) {
  //         // Add height for time group heading + margin + padding
  //         estimatedHeight += 50; // Increased from 30
  //         
  //         // Add height for each activity
  //         timeGroups[timeGroup].forEach(activity => {
  //           // Base activity card height including margins and padding
  //           let activityHeight = 140; // Increased from 100
  //           
  //           // Add more height if there's a description
  //           if (activity.description) {
  //             // More generous height based on description length
  //             const descriptionLength = activity.description.length;
  //             activityHeight += Math.min(250, descriptionLength / 3); // More generous allocation
  //           }
  //           
  //           estimatedHeight += activityHeight;
  //         });
  //       }
  //     });
  //     
  //     return estimatedHeight;
  //   };

  // Create days and activities - immediately after trip summary
  if (itinerary.days && Array.isArray(itinerary.days)) {
    const daysSection = document.createElement('div');
    daysSection.className = 'day-section';
    
    const daysTitle = document.createElement('h3');
    daysTitle.className = 'section-title';
    daysTitle.textContent = 'Daily Itinerary';
    daysSection.appendChild(daysTitle);
    
    documentBody.appendChild(daysSection);
    
    itinerary.days.forEach((day, index) => {
      const dayContainer = document.createElement('div');
      
      // First day starts after the title, all other days start on a new page
      if (index === 0) {
        dayContainer.className = 'day-container';
      } else {
        dayContainer.className = 'day-container force-page-break';
      }
      
      const dayTitle = document.createElement('div');
      dayTitle.className = 'day-title';
      
      // Calculate the date for this day
      const startDate = new Date(itinerary.startDate);
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + index);
      
      dayTitle.innerHTML = `
        <span class="day-number">Day ${day.day || (index + 1)}</span>
        <span class="day-date">${format(currentDate, 'EEEE, MMMM d, yyyy')}</span>
      `;
      dayContainer.appendChild(dayTitle);
      
      // Create day content container
      const dayContent = document.createElement('div');
      dayContent.className = 'day-content';
      
      // Activities for this day
      if (day.activities && day.activities.length > 0) {
        // Group activities by morning, afternoon, evening to save space
        const timeGroups = {
          'Morning': [],
          'Afternoon': [],
          'Evening': []
        };
        
        day.activities.forEach(activity => {
          const time = (activity.time || "").toLowerCase();
          if (time.includes('morning') || (parseInt(time) >= 6 && parseInt(time) < 12)) {
            timeGroups['Morning'].push(activity);
          } else if (time.includes('afternoon') || (parseInt(time) >= 12 && parseInt(time) < 18)) {
            timeGroups['Afternoon'].push(activity);
          } else {
            timeGroups['Evening'].push(activity);
          }
        });
        
        // Create sections for each time group
        Object.keys(timeGroups).forEach(timeGroup => {
          if (timeGroups[timeGroup].length > 0) {
            const timeGroupContainer = document.createElement('div');
            timeGroupContainer.className = 'time-group';
            
            const timeGroupHeading = document.createElement('div');
            timeGroupHeading.className = 'time-group-heading';
            timeGroupHeading.textContent = timeGroup;
            timeGroupContainer.appendChild(timeGroupHeading);
            
            timeGroups[timeGroup].forEach(activity => {
              const activitySection = document.createElement('div');
              activitySection.className = 'activity-section';
              
              // Determine activity type for styling
              let activityType = 'activity';
              if (activity.type) {
                const type = activity.type.toLowerCase();
                if (type.includes('attraction') || type.includes('sight')) {
                  activityType = 'attraction';
                } else if (type.includes('dining') || type.includes('restaurant') || type.includes('food')) {
                  activityType = 'dining';
                } else if (type.includes('transport')) {
                  activityType = 'transportation';
                }
              }
              
              const activityCard = document.createElement('div');
              activityCard.className = `activity-card ${activityType}`;
              
              if (activity.time && !activity.time.toLowerCase().includes(timeGroup.toLowerCase())) {
                const activityTime = document.createElement('div');
                activityTime.className = 'activity-time';
                activityTime.textContent = activity.time;
                activityCard.appendChild(activityTime);
              }
              
              const activityName = document.createElement('div');
              activityName.className = 'activity-name';
              activityName.textContent = activity.title || activity.name || 'Activity';
              activityCard.appendChild(activityName);
              
              if (activity.description) {
                const activityDesc = document.createElement('div');
                activityDesc.className = 'activity-description';
                activityDesc.textContent = activity.description;
                activityCard.appendChild(activityDesc);
              }
              
              const activityMeta = document.createElement('div');
              activityMeta.className = 'activity-meta';
              
              const metaDetails = document.createElement('div');
              
              if (activity.duration) {
                metaDetails.textContent += `Duration: ${activity.duration}`;
              }
              
              if (activity.cost) {
                const costSpan = document.createElement('span');
                costSpan.className = 'cost-indicator';
                costSpan.textContent = activity.cost;
                metaDetails.appendChild(costSpan);
              }
              
              activityMeta.appendChild(metaDetails);
              
              if (activity.type) {
                const typeSpan = document.createElement('span');
                typeSpan.className = 'activity-type';
                typeSpan.textContent = activity.type.charAt(0).toUpperCase() + activity.type.slice(1);
                activityMeta.appendChild(typeSpan);
              }
              
              activityCard.appendChild(activityMeta);
              activitySection.appendChild(activityCard);
              timeGroupContainer.appendChild(activitySection);
            });
            
            dayContent.appendChild(timeGroupContainer);
          }
        });
      } else {
        const emptyDay = document.createElement('div');
        emptyDay.textContent = 'No activities planned for this day.';
        emptyDay.style.fontStyle = 'italic';
        emptyDay.style.color = '#757575';
        emptyDay.style.textAlign = 'center';
        emptyDay.style.padding = '20px';
        dayContent.appendChild(emptyDay);
      }
      
      dayContainer.appendChild(dayContent);
      documentBody.appendChild(dayContainer);
    });
  }
  
  // Add supplementary information section
  const supplementarySection = document.createElement('div');
  supplementarySection.className = 'supplementary-section force-page-break'; // Start on new page
  
  // Add essential travel information if available (now inside supplementary section)
  if (itinerary.travelInfo) {
    const travelInfoSection = document.createElement('div');
    travelInfoSection.className = 'travel-info-section';
    
    const travelInfoTitle = document.createElement('h3');
    travelInfoTitle.className = 'section-title';
    travelInfoTitle.textContent = 'Essential Travel Information';
    travelInfoSection.appendChild(travelInfoTitle);
    
    const travelInfoGrid = document.createElement('div');
    travelInfoGrid.className = 'travel-info-grid';
    
    // Add visa requirements
    if (itinerary.travelInfo.visaRequirements) {
      const infoItem = document.createElement('div');
      infoItem.className = 'travel-info-item';
      
      const infoTitle = document.createElement('div');
      infoTitle.className = 'travel-info-title';
      infoTitle.textContent = 'Visa Requirements';
      
      const infoContent = document.createElement('div');
      infoContent.className = 'travel-info-content';
      infoContent.textContent = itinerary.travelInfo.visaRequirements;
      
      infoItem.appendChild(infoTitle);
      infoItem.appendChild(infoContent);
      travelInfoGrid.appendChild(infoItem);
    }
    
    // Add travel restrictions
    if (itinerary.travelInfo.travelRestrictions) {
      const infoItem = document.createElement('div');
      infoItem.className = 'travel-info-item';
      
      const infoTitle = document.createElement('div');
      infoTitle.className = 'travel-info-title';
      infoTitle.textContent = 'Travel Restrictions';
      
      const infoContent = document.createElement('div');
      infoContent.className = 'travel-info-content';
      infoContent.textContent = itinerary.travelInfo.travelRestrictions;
      
      infoItem.appendChild(infoTitle);
      infoItem.appendChild(infoContent);
      travelInfoGrid.appendChild(infoItem);
    }
    
    // Add health requirements
    if (itinerary.travelInfo.healthRequirements) {
      const infoItem = document.createElement('div');
      infoItem.className = 'travel-info-item';
      
      const infoTitle = document.createElement('div');
      infoTitle.className = 'travel-info-title';
      infoTitle.textContent = 'Health Requirements';
      
      const infoContent = document.createElement('div');
      infoContent.className = 'travel-info-content';
      infoContent.textContent = itinerary.travelInfo.healthRequirements;
      
      infoItem.appendChild(infoTitle);
      infoItem.appendChild(infoContent);
      travelInfoGrid.appendChild(infoItem);
    }
    
    // Add currency information
    if (itinerary.travelInfo.currency) {
      const infoItem = document.createElement('div');
      infoItem.className = 'travel-info-item';
      
      const infoTitle = document.createElement('div');
      infoTitle.className = 'travel-info-title';
      infoTitle.textContent = 'Currency Information';
      
      const infoContent = document.createElement('div');
      infoContent.className = 'travel-info-content';
      infoContent.textContent = itinerary.travelInfo.currency;
      
      infoItem.appendChild(infoTitle);
      infoItem.appendChild(infoContent);
      travelInfoGrid.appendChild(infoItem);
    }
    
    // Add best time to visit
    if (itinerary.travelInfo.bestTimeToVisit) {
      const infoItem = document.createElement('div');
      infoItem.className = 'travel-info-item';
      
      const infoTitle = document.createElement('div');
      infoTitle.className = 'travel-info-title';
      infoTitle.textContent = 'Best Time to Visit';
      
      const infoContent = document.createElement('div');
      infoContent.className = 'travel-info-content';
      infoContent.textContent = itinerary.travelInfo.bestTimeToVisit;
      
      infoItem.appendChild(infoTitle);
      infoItem.appendChild(infoContent);
      travelInfoGrid.appendChild(infoItem);
    }
    
    // Add local customs
    if (itinerary.travelInfo.localCustoms) {
      const infoItem = document.createElement('div');
      infoItem.className = 'travel-info-item';
      
      const infoTitle = document.createElement('div');
      infoTitle.className = 'travel-info-title';
      infoTitle.textContent = 'Local Customs';
      
      const infoContent = document.createElement('div');
      infoContent.className = 'travel-info-content';
      infoContent.textContent = itinerary.travelInfo.localCustoms;
      
      infoItem.appendChild(infoTitle);
      infoItem.appendChild(infoContent);
      travelInfoGrid.appendChild(infoItem);
    }
    
    travelInfoSection.appendChild(travelInfoGrid);
    supplementarySection.appendChild(travelInfoSection);
  }
  
  // Footer with meta information
  const footer = document.createElement('div');
  footer.className = 'footer';
  
  // Logo
  const footerLogo = document.createElement('div');
  footerLogo.className = 'footer-logo';
  footerLogo.textContent = 'roamzap';
  footer.appendChild(footerLogo);
  
  // Created time
  const createdInfo = document.createElement('div');
  createdInfo.textContent = `Generated on ${format(new Date(), 'MMMM d, yyyy')}`;
  footer.appendChild(createdInfo);
  
  // Website info
  const websiteInfo = document.createElement('div');
  websiteInfo.textContent = 'www.roamzap.com';
  websiteInfo.style.fontWeight = '500';
  footer.appendChild(websiteInfo);
  
  // Disclaimer
  const disclaimer = document.createElement('div');
  disclaimer.className = 'print-disclaimer';
  disclaimer.textContent = 'This itinerary is a suggestion and all activities are subject to availability, weather conditions, and local changes.';
  footer.appendChild(disclaimer);
  
  supplementarySection.appendChild(footer);
  
  // Add the supplementary section to the document body
  documentBody.appendChild(supplementarySection);
  
  // Add the document body to the print content
  printContent.appendChild(documentBody);
  
  // Add to document and print
  document.body.appendChild(printContent);
  window.print();
  
  // Clean up
  setTimeout(() => {
    document.body.removeChild(printContent);
    document.head.removeChild(style);
  }, 1000);
}; 